const createTab = ({
    uid,
    type,
    label,
    docs,
}: {
    uid: string;
    type: string;
    label: string;
    docs?: { title?: string; url?: string };
}) => ({
    tab: { uid, label, docs },
    content: { uid, type },
});

export const logsTabKey = "logs";
export const logsContentType = "logsListing";
export const logsTab = createTab({ uid: logsTabKey, label: "Logs", type: logsContentType });

export const usedByContentKey = "usedByList";
export const usedByTab = createTab({ uid: "usedBy", type: usedByContentKey, label: "Used by" });

// Generic reverse-reference ("used by") tab backed by the POST /references-to
// endpoint (see ReferencesUsedByList). Distinct content key so the render switch
// in ResourcesSingle can pick the unified grouped component without touching the
// bespoke per-relationship usedByContentKey tabs.
export const referencesUsedByContentKey = "referencesUsedByList";
export const referencesUsedByTab = createTab({
    uid: "referencesUsedBy",
    type: referencesUsedByContentKey,
    label: "Used by",
});

export const zoneMembersContentKey = "zoneMemberList";
export const zoneMembersTab = createTab({
    uid: "zoneMembers",
    type: zoneMembersContentKey,
    label: "Records in zone",
});

export const connectorEgressPoliciesContentKey = "connectorEgressPolicies";
export const connectorEgressPoliciesTab = createTab({
    uid: "connectorEgressPolicies",
    type: connectorEgressPoliciesContentKey,
    label: "Egress policies",
});

export const holdingTheLockTabKey = "holdingTheLock";
export const holdingTheLockTab = createTab({
    uid: holdingTheLockTabKey,
    label: "Holding the lock",
    type: usedByContentKey,
});

export const waitingForLockTab = createTab({
    uid: "waitingForLock",
    label: "Waiting for lock",
    type: usedByContentKey,
});

export const usedByWebhooksList = "usedByWebhooksList";
export const usedByWebhooksTab = createTab({
    uid: usedByWebhooksList,
    label: "Webhooks",
    type: usedByContentKey,
});
export const usedBySchedulesList = "usedBySchedulesList";
export const usedBySchedulesTab = createTab({
    uid: usedBySchedulesList,
    label: "Schedules",
    type: usedByContentKey,
});

export const inputSchemaTabKey = "inputSchemaTab";
export const inputSchemaContentType = "inputSchema";
export const inputSchemaTab = createTab({
    uid: inputSchemaTabKey,
    label: "Input schema",
    type: inputSchemaContentType,
    docs: {
        title: "Schema creation and validation",
        url: "/docs/schema-creation-and-validation#input-and-output-schema",
    },
});
// Scheduler-only: the schema a scheduler AUTHOR edits is its
// `configuration_schema` (the shape of the `configuration` blob a schedule
// passes), not a free `input_schema` — the scheduler screen remaps the editable
// schema tab to `configuration_schema` (see ResourcesSingle `inputSchemaFieldName`).
// It therefore carries its own label so it is not presented as a free
// "Input schema"; it reuses the editable inputSchema content path.
// SCHEMA records: the schema itself is the record's `value`. The tab reuses the
// editable inputSchema content path (builder, form view, Enrich); the screen
// binds it to `value` (see ResourcesSingle `inputSchemaFieldName`).
export const schemaValueTabKey = "schemaTab";
export const schemaValueTab = createTab({
    uid: schemaValueTabKey,
    label: "Schema",
    type: inputSchemaContentType,
    docs: {
        title: "Schema creation and validation",
        url: "/docs/schema-creation-and-validation#schema-resource",
    },
});
export const configurationSchemaTabKey = "configurationSchemaTab";
export const configurationSchemaTab = createTab({
    uid: configurationSchemaTabKey,
    label: "Configuration schema",
    type: inputSchemaContentType,
    docs: {
        title: "Scheduling",
        url: "/docs/scheduling#scheduler-configuration-schema",
    },
});

// Scheduler-only: a READ-ONLY view of the fixed input envelope a SCHEDULE
// execution receives ({schedule_id, configuration, flow_id}, where
// `configuration` = the scheduler's own configuration_schema). This envelope is
// hardcoded by the platform (resources/execution.py) and is not author-editable,
// so it is shown read-only via SchedulerInputSchemaTab.
export const schedulerInputSchemaContentType = "schedulerInputSchema";
export const schedulerInputSchemaTabKey = "schedulerInputSchemaTab";
export const schedulerInputSchemaTab = createTab({
    uid: schedulerInputSchemaTabKey,
    label: "Input schema",
    type: schedulerInputSchemaContentType,
    docs: {
        title: "Schema creation and validation",
        url: "/docs/schema-creation-and-validation#input-and-output-schema",
    },
});

export const outputSchemaTabKey = "outputSchemaTab";
export const outputSchemaContentType = "outputSchema";
export const outputSchemaTab = createTab({
    uid: outputSchemaTabKey,
    label: "Output schema",
    type: outputSchemaContentType,
    docs: {
        title: "Schema creation and validation",
        url: "/docs/schema-creation-and-validation#input-and-output-schema",
    },
});

export const wrappersTabKey = "resourceWrappersTab";
export const wrappersContentType = "resourceWrappers";
export const wrappersTab = createTab({
    uid: wrappersTabKey,
    label: "Wrappers",
    type: wrappersContentType,
});

export const valueAndSchemaTabKey = "valueAndSchemaTabKey";
export const valueAndSchemaContentType = "valueAndSchema";
export const valueAndSchemaTab = createTab({
    uid: valueAndSchemaTabKey,
    label: "Configuration",
    type: valueAndSchemaContentType,
});

export const metadataTabKey = "metadataTab";
export const metadataContentType = "metadata";
export const metadataTab = createTab({
    uid: metadataTabKey,
    type: metadataContentType,
    label: "Metadata",
});

export const commentsTabKey = "commentsTab";
export const commentsContentType = "comments";
export const commentsTab = createTab({
    uid: commentsTabKey,
    type: commentsContentType,
    label: "Comments",
});

export const valueAndSchemaInput = "valueAndSchemaInput";
export const valueAndSchemaOutput = "valueAndSchemaOutput";
export const valueAndSchema = "valueAndSchema";

export const schedulerKey = "allSchedulers";

export const executionRelatedRecordsTabKey = "executionRelatedRecordsTab";
export const executionRelatedRecordsContentType = "executionRelatedRecords";
export const executionRelatedRecordsTab = createTab({
    uid: executionRelatedRecordsTabKey,
    label: "Related records",
    type: executionRelatedRecordsContentType,
});
